// src/components/leads/LeadMap.tsx
import React, { useMemo, useState, useCallback, useEffect, useRef } from 'react';
import { Marker, InfoWindow } from '@react-google-maps/api';
import BaseMap, { MapPosition } from '../map/BaseMap';
import GoogleMapProvider, { useGoogleMapContext } from '../map/GoogleMapProvider';
import { Lead, LeadStatus } from '../../types/lead.types';
import { Loader2 } from 'lucide-react';

interface LeadMapProps {
  leads: Lead[];
  onLeadSelect: (lead: Lead) => void;
}

interface LeadWithPosition {
  lead: Lead;
  position: MapPosition;
  source: 'gps' | 'geocoded';
}

// Status color mapping for markers
const STATUS_COLORS: Record<LeadStatus, string> = {
  uncontacted: '#6B7280', // gray
  cold: '#3B82F6',        // blue
  warm: '#F59E0B',        // amber
  hot: '#EF4444',         // red
  enrolled: '#10B981',    // green
  lost: '#6B7280',        // gray
  dnc: '#1F2937',         // dark gray
};

const STATUS_LABELS: Record<LeadStatus, string> = {
  uncontacted: 'Uncontacted',
  cold: 'Cold',
  warm: 'Warm',
  hot: 'Hot',
  enrolled: 'Enrolled',
  lost: 'Lost',
  dnc: 'Do Not Contact',
};

// Create SVG marker icon
const createMarkerIcon = (color: string): string => {
  const svg = `
    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="36" viewBox="0 0 24 36">
      <path fill="${color}" stroke="#FFFFFF" stroke-width="2" d="M12 0C5.4 0 0 5.4 0 12c0 9 12 24 12 24s12-15 12-24c0-6.6-5.4-12-12-12z"/>
      <circle fill="#FFFFFF" cx="12" cy="12" r="5"/>
    </svg>
  `;
  return `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`;
};

// Cache for geocoded addresses to avoid re-geocoding
const geocodeCache = new Map<string, MapPosition | null>();

const LeadMapContent: React.FC<LeadMapProps> = ({ leads, onLeadSelect }) => {
  const { isLoaded } = useGoogleMapContext();
  const [hoveredLead, setHoveredLead] = useState<Lead | null>(null);
  const [leadsWithPositions, setLeadsWithPositions] = useState<LeadWithPosition[]>([]);
  const [isGeocoding, setIsGeocoding] = useState(false);
  const [geocodeProgress, setGeocodeProgress] = useState({ done: 0, total: 0 });
  const geocoderRef = useRef<google.maps.Geocoder | null>(null);

  // Build full address string for geocoding
  const buildAddressString = useCallback((lead: Lead): string => {
    const parts = [
      lead.address,
      lead.city,
      lead.state,
      lead.zipCode
    ].filter(Boolean);
    return parts.join(', ');
  }, []);

  // Geocode a single address
  const geocodeAddress = useCallback(async (address: string): Promise<MapPosition | null> => {
    // Check cache first
    if (geocodeCache.has(address)) {
      return geocodeCache.get(address) || null;
    }

    if (!geocoderRef.current) {
      geocoderRef.current = new google.maps.Geocoder();
    }

    return new Promise((resolve) => {
      geocoderRef.current!.geocode({ address }, (results, status) => {
        if (status === 'OK' && results && results[0]) {
          const location = results[0].geometry.location;
          const position: MapPosition = {
            lat: location.lat(),
            lng: location.lng(),
          };
          geocodeCache.set(address, position);
          resolve(position);
        } else {
          geocodeCache.set(address, null);
          resolve(null);
        }
      });
    });
  }, []);

  // Process leads and geocode addresses
  useEffect(() => {
    if (!isLoaded || leads.length === 0) {
      setLeadsWithPositions([]);
      return;
    }

    const processLeads = async () => {
      setIsGeocoding(true);
      const results: LeadWithPosition[] = [];

      // First, add all leads that already have GPS coordinates
      const leadsWithGPS = leads.filter(lead =>
        lead.gpsLocation?.latitude && lead.gpsLocation?.longitude
      );

      for (const lead of leadsWithGPS) {
        results.push({
          lead,
          position: {
            lat: lead.gpsLocation!.latitude,
            lng: lead.gpsLocation!.longitude,
          },
          source: 'gps',
        });
      }

      // Find leads that need geocoding (have address but no GPS)
      const leadsNeedingGeocode = leads.filter(lead =>
        !lead.gpsLocation?.latitude &&
        !lead.gpsLocation?.longitude &&
        lead.address // Must have at least a street address
      );

      setGeocodeProgress({ done: 0, total: leadsNeedingGeocode.length });

      // Geocode addresses in batches to avoid rate limiting
      const BATCH_SIZE = 5;
      const BATCH_DELAY = 200; // ms between batches

      for (let i = 0; i < leadsNeedingGeocode.length; i += BATCH_SIZE) {
        const batch = leadsNeedingGeocode.slice(i, i + BATCH_SIZE);

        const batchPromises = batch.map(async (lead) => {
          const addressString = buildAddressString(lead);
          if (!addressString) return null;

          const position = await geocodeAddress(addressString);
          if (position) {
            return { lead, position, source: 'geocoded' as const };
          }
          return null;
        });

        const batchResults = await Promise.all(batchPromises);

        for (const result of batchResults) {
          if (result) {
            results.push(result);
          }
        }

        setGeocodeProgress({ done: Math.min(i + BATCH_SIZE, leadsNeedingGeocode.length), total: leadsNeedingGeocode.length });

        // Add delay between batches to avoid rate limiting
        if (i + BATCH_SIZE < leadsNeedingGeocode.length) {
          await new Promise(resolve => setTimeout(resolve, BATCH_DELAY));
        }
      }

      setLeadsWithPositions(results);
      setIsGeocoding(false);
    };

    processLeads();
  }, [leads, isLoaded, buildAddressString, geocodeAddress]);

  // Calculate map center based on leads
  const mapCenter = useMemo((): MapPosition => {
    if (leadsWithPositions.length === 0) {
      return { lat: 39.8283, lng: -98.5795 }; // Default to center of US
    }

    const sumLat = leadsWithPositions.reduce((sum, item) => sum + item.position.lat, 0);
    const sumLng = leadsWithPositions.reduce((sum, item) => sum + item.position.lng, 0);

    return {
      lat: sumLat / leadsWithPositions.length,
      lng: sumLng / leadsWithPositions.length,
    };
  }, [leadsWithPositions]);

  // Calculate appropriate zoom based on spread of markers
  const mapZoom = useMemo(() => {
    if (leadsWithPositions.length <= 1) return 12;

    const lats = leadsWithPositions.map(item => item.position.lat);
    const lngs = leadsWithPositions.map(item => item.position.lng);

    const latSpread = Math.max(...lats) - Math.min(...lats);
    const lngSpread = Math.max(...lngs) - Math.min(...lngs);
    const maxSpread = Math.max(latSpread, lngSpread);

    if (maxSpread > 10) return 5;
    if (maxSpread > 5) return 7;
    if (maxSpread > 2) return 9;
    if (maxSpread > 0.5) return 11;
    if (maxSpread > 0.1) return 13;
    return 14;
  }, [leadsWithPositions]);

  const handleMarkerClick = useCallback((lead: Lead) => {
    onLeadSelect(lead);
  }, [onLeadSelect]);

  // Get position for hovered lead
  const hoveredPosition = useMemo(() => {
    if (!hoveredLead) return null;
    const item = leadsWithPositions.find(p => p.lead.id === hoveredLead.id);
    return item?.position || null;
  }, [hoveredLead, leadsWithPositions]);

  // Count by source
  const counts = useMemo(() => {
    const gpsCount = leadsWithPositions.filter(p => p.source === 'gps').length;
    const geocodedCount = leadsWithPositions.filter(p => p.source === 'geocoded').length;
    return { gps: gpsCount, geocoded: geocodedCount, total: leadsWithPositions.length };
  }, [leadsWithPositions]);

  if (isGeocoding && leadsWithPositions.length === 0) {
    return (
      <div className="flex items-center justify-center h-96 bg-gray-50 rounded-lg border border-gray-200">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin text-indigo-600 mx-auto mb-3" />
          <p className="text-gray-600 font-medium">Loading map locations...</p>
          <p className="text-gray-400 text-sm mt-1">
            Geocoding addresses: {geocodeProgress.done} of {geocodeProgress.total}
          </p>
        </div>
      </div>
    );
  }

  if (leadsWithPositions.length === 0 && !isGeocoding) {
    return (
      <div className="flex items-center justify-center h-96 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
        <div className="text-center">
          <p className="text-gray-500 text-lg font-medium">No leads with location data</p>
          <p className="text-gray-400 mt-1">Leads need a valid address or GPS coordinates to appear on the map</p>
          <p className="text-gray-400 text-sm mt-2">
            {leads.length} total lead{leads.length !== 1 ? 's' : ''} found
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="relative">
      {/* Map Legend */}
      <div className="absolute top-4 right-4 z-10 bg-white rounded-lg shadow-lg p-3">
        <p className="text-xs font-semibold text-gray-700 mb-2">Lead Status</p>
        <div className="space-y-1">
          {(Object.keys(STATUS_COLORS) as LeadStatus[]).map(status => (
            <div key={status} className="flex items-center gap-2">
              <div
                className="w-3 h-3 rounded-full"
                style={{ backgroundColor: STATUS_COLORS[status] }}
              />
              <span className="text-xs text-gray-600">{STATUS_LABELS[status]}</span>
            </div>
          ))}
        </div>
        <div className="mt-2 pt-2 border-t border-gray-200 space-y-1">
          <p className="text-xs text-gray-500">
            {counts.total} of {leads.length} leads shown
          </p>
          {counts.gps > 0 && (
            <p className="text-xs text-gray-400">
              {counts.gps} with GPS
            </p>
          )}
          {counts.geocoded > 0 && (
            <p className="text-xs text-gray-400">
              {counts.geocoded} from address
            </p>
          )}
        </div>
        {isGeocoding && (
          <div className="mt-2 pt-2 border-t border-gray-200 flex items-center gap-2">
            <Loader2 className="h-3 w-3 animate-spin text-indigo-600" />
            <span className="text-xs text-gray-500">Loading more...</span>
          </div>
        )}
      </div>

      <BaseMap
        center={mapCenter}
        zoom={mapZoom}
        height="600px"
        className="rounded-lg shadow"
      >
        {leadsWithPositions.map(({ lead, position }) => (
          <Marker
            key={lead.id}
            position={position}
            icon={{
              url: createMarkerIcon(STATUS_COLORS[lead.status]),
              scaledSize: new google.maps.Size(24, 36),
              anchor: new google.maps.Point(12, 36),
            }}
            onClick={() => handleMarkerClick(lead)}
            onMouseOver={() => setHoveredLead(lead)}
            onMouseOut={() => setHoveredLead(null)}
          />
        ))}

        {/* Info Window for hovered lead */}
        {hoveredLead && hoveredPosition && (
          <InfoWindow
            position={hoveredPosition}
            options={{
              pixelOffset: new google.maps.Size(0, -36),
              disableAutoPan: true,
            }}
            onCloseClick={() => setHoveredLead(null)}
          >
            <div className="p-1 min-w-[150px]">
              <p className="font-semibold text-gray-900">{hoveredLead.parentName}</p>
              <p className="text-xs text-gray-500 mt-1">
                {hoveredLead.address}
              </p>
              {hoveredLead.city && (
                <p className="text-xs text-gray-500">
                  {hoveredLead.city}, {hoveredLead.state} {hoveredLead.zipCode}
                </p>
              )}
              <div className="flex items-center gap-2 mt-2">
                <span
                  className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium"
                  style={{
                    backgroundColor: `${STATUS_COLORS[hoveredLead.status]}20`,
                    color: STATUS_COLORS[hoveredLead.status]
                  }}
                >
                  {STATUS_LABELS[hoveredLead.status]}
                </span>
              </div>
              <p className="text-xs text-gray-400 mt-2">Click to view details</p>
            </div>
          </InfoWindow>
        )}
      </BaseMap>
    </div>
  );
};

// Wrap with GoogleMapProvider
const LeadMap: React.FC<LeadMapProps> = (props) => {
  return (
    <GoogleMapProvider>
      <LeadMapContent {...props} />
    </GoogleMapProvider>
  );
};

export default LeadMap;
